import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { useTranslation } from "react-i18next";
import SubmitButton from "../../components/ui/SubmitButton";
import {
  fetchSiteSettings,
  updateSiteSettings,
} from "../../../store/custom-actions/settings-actions/siteSettingsSlicer";
import { EMAIL_TEMPLATE_TABS } from "./emailTemplateFieldsConfig";
import EmailTemplateField from "./EmailTemplateField";
import EmailTemplatePreview from "./EmailTemplatePreview";
import {
  buildSavePayload,
  EMAIL_TEMPLATE_OPTION_NAMES,
  EMAIL_TEMPLATE_UPLOAD_KEYS,
  getDefaultForField,
  getDefaultsForFieldNames,
  getOptionForMedia,
  getOptionValue,
  getTabById,
  GLOBAL_VARIABLES,
  hasDefaultForField,
  subsectionHasDefaults,
} from "./emailTemplateUtils";

function EmailTemplatesPage() {
  const { t } = useTranslation();
  const { loading: submitLoading } = useSelector((state) => state.siteSettingsSlicer);
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(EMAIL_TEMPLATE_TABS[0].id);
  const [pageLoading, setPageLoading] = useState(true);
  const [mediaData, setMediaData] = useState({ invoice_logo: [] });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = useForm();

  const activeTabConfig = getTabById(activeTab);

  const fetchTemplateData = useCallback(async () => {
    setPageLoading(true);
    try {
      const res = await dispatch(
        fetchSiteSettings({
          option_name: EMAIL_TEMPLATE_OPTION_NAMES,
          upload_keys: EMAIL_TEMPLATE_UPLOAD_KEYS,
        })
      );
      const data = res.meta?.requestStatus === "fulfilled" ? res.payload : null;
      if (!data) return;

      EMAIL_TEMPLATE_TABS.forEach((tab) => {
        tab.subsections.forEach((section) => {
          section.fields.forEach(({ name }) => {
            const value = getOptionValue(data, name);
            if (value !== undefined) {
              setValue(name, value);
            }
          });
        });
      });

      setMediaData({
        invoice_logo: getOptionForMedia(data, "invoice_logo"),
      });
    } finally {
      setPageLoading(false);
    }
  }, [dispatch, setValue]);

  useEffect(() => {
    fetchTemplateData();
  }, [fetchTemplateData]);

  const applyDefaults = useCallback(
    (defaults) => {
      Object.entries(defaults).forEach(([name, value]) => {
        setValue(name, value, { shouldDirty: true });
      });
    },
    [setValue]
  );

  const onSubmit = async (formData) => {
    const payload = buildSavePayload(formData);
    const result = await dispatch(updateSiteSettings(payload));
    if (updateSiteSettings.fulfilled.match(result)) {
      toast.success(t(result.payload?.message || "Email templates saved successfully"), {
        position: "top-right",
        autoClose: 3000,
      });
      await fetchTemplateData();
    }
  };

  const handleRestoreField = (fieldName, fieldLabel) => {
    const defaultValue = getDefaultForField(fieldName);
    if (defaultValue === undefined) return;

    applyDefaults({ [fieldName]: defaultValue });
    toast.info(
      t('Default restored for "{{label}}" — click Save to apply', { label: t(fieldLabel) }),
      { position: "top-right", autoClose: 3000 }
    );
  };

  const handleRestoreSubsection = (section) => {
    const fieldNames = section.fields.map((field) => field.name);
    const defaults = getDefaultsForFieldNames(fieldNames);
    if (!Object.keys(defaults).length) return;

    Swal.fire({
      title: t("Restore default texts?"),
      text: t('This will replace all fields in "{{section}}" with default Arabic texts. You still need to click Save.', {
        section: t(section.label),
      }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("Restore"),
      cancelButtonText: t("Cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;
      applyDefaults(defaults);
      toast.info(
        t('Defaults restored for "{{section}}" — click Save to apply', { section: t(section.label) }),
        { position: "top-right", autoClose: 4000 }
      );
    });
  };

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
  };

  return (
    <>
      <PageTitle
        motherMenu={t("Settings")}
        activeMenu={t("Email Templates")}
        customClasses="mb-0"
      />

      <div className="row">
        <div className="col-xl-3 col-lg-4 mb-4">
          <div className="card">
            <div className="card-body p-0">
              <div className="px-4 py-3 border-bottom">
                <i className="bi bi-envelope-paper text-primary fs-4" />
                <p className="small text-muted mb-0 mt-2">
                  {t("Customize order and payment email texts sent to customers and admins.")}
                </p>
              </div>
              <nav className="nav flex-column">
                {EMAIL_TEMPLATE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabChange(tab.id)}
                    className={`nav-link text-start d-flex align-items-start gap-2 py-3 px-4 border-0 rounded-0 border-bottom ${
                      activeTab === tab.id ? "bg-primary text-white" : ""
                    }`}
                    aria-current={activeTab === tab.id ? "true" : undefined}
                  >
                    <i className={`${tab.icon} mt-1`} />
                    <span>
                      <span className="d-block fw-semibold">{t(tab.label)}</span>
                      <small className={activeTab === tab.id ? "text-white-50" : "text-muted"}>
                        {t(tab.description)}
                      </small>
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">
                <i className="bi bi-braces me-2 text-info" />
                {t("Available Variables")}
              </h6>
              <ul className="list-unstyled mb-0">
                {GLOBAL_VARIABLES.map(({ key, label }) => (
                  <li key={key} className="mb-2 pb-2 border-bottom">
                    <code>{key}</code>
                    <span className="d-block small text-muted">{t(label)}</span>
                  </li>
                ))}
              </ul>
              <div className="alert alert-info mt-3 mb-0 py-2 small">
                <i className="bi bi-lightbulb me-1" />
                {t("Templates support HTML. Avoid script tags; use paragraphs, links, and inline styles.")}
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-9 col-lg-8">
          <form id="email-templates-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <SubmitButton
                isLoading={submitLoading}
                form="email-templates-form"
                label="Save Email Templates"
                loadingLabel="Saving..."
              />
              {isDirty && (
                <span className="badge bg-warning text-dark ms-auto">
                  <i className="bi bi-pencil me-1" />
                  {t("Unsaved changes")}
                </span>
              )}
            </div>

            <div className="card mb-4">
              <div className="card-body py-3">
                <h5 className="mb-1">
                  <i className={`${activeTabConfig?.icon} me-2 text-primary`} />
                  {t(activeTabConfig?.label)}
                </h5>
                <p className="text-muted small mb-0">{t(activeTabConfig?.description)}</p>
              </div>
            </div>

            {pageLoading ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted mb-0">{t("Loading...")}</p>
                </div>
              </div>
            ) : (
              activeTabConfig?.subsections.map((section) => (
                <div key={section.id} className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <h6 className="mb-0">
                        <i className="bi bi-envelope me-2" />
                        {t(section.label)}
                      </h6>
                      {section.hint && (
                        <small className="text-muted d-block mt-1">
                          <i className="bi bi-info-circle me-1" />
                          {t(section.hint)}
                        </small>
                      )}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <EmailTemplatePreview
                        sectionLabel={section.label}
                        fields={section.fields}
                        watch={watch}
                      />
                      {subsectionHasDefaults(section) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleRestoreSubsection(section)}
                          disabled={submitLoading}
                        >
                          <i className="bi bi-arrow-counterclockwise me-1" />
                          {t("Restore all in this email")}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {section.fields.map((field) => (
                        <EmailTemplateField
                          key={field.name}
                          field={field}
                          register={register}
                          errors={errors}
                          watch={watch}
                          setValue={setValue}
                          mediaDefault={
                            field.name === "invoice_logo" ? mediaData.invoice_logo : []
                          }
                          hasDefault={hasDefaultForField(field.name)}
                          onRestoreField={handleRestoreField}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </form>
        </div>
      </div>
    </>
  );
}

export default EmailTemplatesPage;
